/* iceberg-ui */
import { IconWithVariant } from '@auviknetworks/iceberg-ui';

/*  MUI  */
import { Box, Button, CircularProgress, Typography } from '@mui/material';

/*  Shared  */
import { DarkTooltip } from 'application/Shared/Components/Tooltips';
import { FontSizes } from 'application/Shared/Constants';
import { Helpers, hmsFormatter } from 'application/Shared/Helpers';

/* Hooks */
import { useSession } from 'hooks/redux/Selectors/session';
import { useTenantInfoByTenantId } from 'hooks/redux/Selectors/tenants';

/*  React  */
import React, { useEffect, useRef, useState } from 'react';
import { CSVLink } from 'react-csv';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';

/*  Constants  */
const { isset } = Helpers;
const DEFAULT_MAX_EXPORT_ROWS = 40000;

const transformString = (name) => {
    if (!name) return '';

    return name
        .trim()
        .replaceAll(' ', '_')
        .toLowerCase()
        .replaceAll(/(\.|,)/g, '');
};

const getCsvExportCustomerName = ({ clientName, partnerName }) => {
    const partner = transformString(partnerName);
    const client = transformString(clientName);

    if (!partner && !client) return '';

    if (!client) {
        return partner;
    }

    if (!partner) {
        return client;
    }

    return `${partner}-${client}`;
};

const getCsvFilename = ({ clientName, csvExportFileName, partnerName, t }) => {
    const csvExportCustomerName = getCsvExportCustomerName({
        clientName,
        partnerName
    });

    let csvFilename =
        csvExportCustomerName !== '' ? `${csvExportCustomerName}-` : '';
    csvFilename += `${t('Common.report')}-${csvExportFileName}-`;
    csvFilename += `${new Date().getFullYear().toString().padStart(2, '0')}-`;
    csvFilename += `${(new Date().getMonth() + 1)
        .toString()
        .padStart(2, '0')}-`;
    csvFilename += `${new Date().getDate().toString().padStart(2, '0')}_`;
    csvFilename += `${new Date().getHours().toString().padStart(2, '0')}-`;
    csvFilename += `${new Date().getMinutes().toString().padStart(2, '0')}_`;
    csvFilename += new Date().getSeconds().toString().padStart(2, '0');
    return csvFilename;
};

export const ManagedTableCSVExport = ({
    csvBuildOnRequest,
    csvData,
    csvExport,
    csvExportFields,
    csvExportFileName,
    csvIconOnly,
    'data-trackid': dataTrackid,
    dataAPI,
    disabled,
    maxExportRows,
    rowCount,
    rowData
}) => {
    const { t } = useTranslation();
    const session = useSession();
    const { clientId, partnerId } = useParams();

    const partnerTenantInfo = useTenantInfoByTenantId(partnerId);
    const clientTenantInfo = useTenantInfoByTenantId(clientId);

    const partnerName = partnerTenantInfo?.tenant?.name;
    const clientName = clientTenantInfo?.tenant?.name;

    const [csvReady, setCsvReady] = useState(true);
    const [csvFilename, setCsvFilename] = useState('');
    const [exportData, setExportData] = useState(null);
    const csvInstance = useRef();

    // Deal with https://github.com/react-csv/react-csv/issues/189
    useEffect(() => {
        if (exportData && csvInstance.current && csvInstance.current.link) {
            setTimeout(() => {
                csvInstance.current.link.click();
                setExportData(null);
            });
        }
    }, [exportData]);

    const preExportCSV = async () => {
        let currentExportData = csvData;
        if (!csvData && csvBuildOnRequest) {
            dataAPI.addParameter('csv', true);
            const response = await dataAPI.getResults();

            currentExportData = response;
        } else if (!csvData) {
            currentExportData = JSON.parse(JSON.stringify(rowData));
        }

        currentExportData = currentExportData.map((r) => {
            const row = r;

            csvExportFields.forEach((field) => {
                const { rowKeyAndDataPath, key, transform } = field;

                if (!isset(row[key]) && transform !== 'listExtract') {
                    return;
                }

                // If transform is listExtract, val is undefined because row.key does not exist yet.
                // key will be a new property of row to be set below.
                let val = row[key];

                // Convert Object to JSON
                if (typeof val === 'object') {
                    val = JSON.stringify(val);
                    row[key] = val.replace(/"/g, '""');
                    return;
                }

                if (transform === 'hms') {
                    val = parseInt(val, 10);
                    if (val === 0 || Number.isNaN(val)) {
                        row[key] = '-';
                        return;
                    }

                    row[key] = hmsFormatter(val);
                }

                if (transform === 'bool_YN') {
                    row[key] = val ? 'Y' : 'N';
                }

                /*
                    listExtract is used to extract a list of strings from an array.
                    The `key` parameter for this transform is the name of the new property to be added to the row
                    and should be different than the original key where the data is found.

                    The `rowKeyAndDataPath` parameter is used to determine how to extract the data from the array.
                    It can handle an array of strings or an array of objects (even deeply nested ones).
                    rowKeyAndDataPath follows the pattern of `keyToData:path.to.data`.
                    Here are some examples.

                    For `rowKeyAndDataPath: 'aliases'`, `aliases` would be the key to find the data from the row
                    and there is no path, so the array is already just strings, and they are joined.
                        Example:
                            row input: `{ aliases: ['user1', 'user2'] }`
                            rowKeyAndDataPath: 'aliases'
                            output: 'user1, user2'

                    For `rowKeyAndDataPath: 'users:name'`,
                    `users` would be the key, and `name` would be the path in each object.
                        Example:
                            row input: `{ users: [{ name: 'user1' }, { name: 'user2' }] }`
                            rowKeyAndDataPath: 'users:name'
                            output: 'user1, user2'

                    For `rowKeyAndDataPath: 'browsers:application.name'` (nested objects)
                        `browsers` would be the key, and `application` and `name` would be the paths to find the nested strings.
                        Example:
                            row input: `{ browsers: [{ application: { name: 'chrome' } }, { application: { name: 'firefox' } }] }`
                            rowKeyAndDataPath: 'browsers:application.name'
                            output: 'chrome, firefox'
                */
                if (transform === 'listExtract') {
                    const [rowKey, possiblePathKeys] =
                        rowKeyAndDataPath.split(':');

                    const pathKeys = possiblePathKeys
                        ? possiblePathKeys.split('.')
                        : [];

                    row[key] = '';
                    if (row[rowKey] && Array.isArray(row[rowKey])) {
                        row[key] = row[rowKey];
                        if (pathKeys.length) {
                            // for each row, we need to iterate over EACH path key to extract the final string value.
                            // if pathKey = ['name'] => { name: 'ian'}
                            // if pathKey = ['application', 'name'] => { application:  { name: 'chrome' } }
                            row[key] = row[key].map((obj) =>
                                pathKeys.reduce(
                                    (acc, pathKey) => acc?.[pathKey],
                                    obj
                                )
                            );
                        }

                        row[key] = row[key]
                            .filter(Boolean) // just in case any names are falsy, let's remove them.
                            .join(', ');
                    }
                }

                if (typeof val === 'string') {
                    // Escape existing quotes by doubling them
                    val = val.replace(/"/g, '""');
                    row[key] = val;
                }
            });

            return row;
        });

        // Set CSV Filename
        setCsvFilename(
            getCsvFilename({
                clientName,
                csvExportFileName,
                partnerName,
                t
            })
        );
        setExportData(currentExportData);
    };

    const onDownloadCSVClick = async () => {
        setCsvReady(false);
        await preExportCSV();
        setCsvReady(true);
    };

    if (!csvExport) {
        return null;
    }

    // We only care about max export rows if the CSV data is being generated by a separate API call on the backend.
    const maxExportRowsExceeded =
        csvBuildOnRequest &&
        // If maxExportRows is passed as a prop and is > 0, use that value. Otherwise use the default constant.
        (maxExportRows > 0
            ? rowCount > maxExportRows
            : rowCount > DEFAULT_MAX_EXPORT_ROWS);

    const disableExport =
        rowCount === 0 ||
        (!session?.sessionSettings?.expiredTrial &&
            session?.sessionSettings?.inTrial) ||
        maxExportRowsExceeded;

    return (
        <Box
            display="flex"
            alignItems="center"
            data-testid="managedTableCsvExport"
            className="no-print"
        >
            <DarkTooltip
                arrow
                // We disable the button when rowCount is 0, but if it's disabled for that reason,
                // we don't want to show a tooltip when it's related to no data.
                {...(rowCount !== 0 && disableExport
                    ? {
                          title: maxExportRowsExceeded
                              ? t(
                                    'Tooltips.exportFunctionality.disabled_max_rows'
                                )
                              : t('Tooltips.exportFunctionality.disabled')
                      }
                    : {})}
            >
                <Box
                    display="flex"
                    alignItems="center"
                    data-testid="managedTableCsvExportBtnBox"
                >
                    <Button
                        disabled={
                            disabled || Boolean(disableExport) || !csvReady
                        }
                        color="primary"
                        data-testid="managedTableCsvExportBtn"
                        data-trackid={`${dataTrackid}_managedTableCsvExportBtn`}
                        onClick={() => onDownloadCSVClick()}
                        size="anmSmall"
                        variant="outlined"
                    >
                        {csvIconOnly && (
                            <IconWithVariant src="Csv" size={FontSizes[1]} />
                        )}
                        {!csvIconOnly && (
                            <Typography>{t('Common.export')}</Typography>
                        )}
                    </Button>
                    {!csvReady && (
                        <CircularProgress
                            size="24px"
                            sx={{ margin: '4px', marginLeft: '8px' }}
                        />
                    )}
                </Box>
            </DarkTooltip>
            {exportData && (
                <CSVLink
                    asyncOnClick
                    data={exportData}
                    filename={csvFilename}
                    headers={csvExportFields}
                    ref={csvInstance}
                />
            )}
        </Box>
    );
};
